// import "react-native-gesture-handler";

import * as React from "react";
import { AppState, Platform } from "react-native";
import {
  NavigationContainer,
  DefaultTheme,
  createNavigationContainerRef,
} from "@react-navigation/native";
import { StatusBar } from "expo-status-bar";
import RootNavigation from "./src/navigation/navigation";
import { useFonts } from "expo-font";
import { connectToDevTools } from "react-devtools-core";
import store from "./src/store";
import { Provider, useDispatch, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { persistor } from "./src/store";
import { LogBox } from "react-native";
import NetInfo from "@react-native-community/netinfo";
import api from "./src/services/api";
import { QueryClientProvider, onlineManager, useQuery } from "@tanstack/react-query";
import queryClient from "./src/utils/queryClient";
import { getOverview } from "./src/services/profile";
import { setIsDev, setIsDoctor, setLoggedOut } from "./src/reducers/auth/user";
import { resetFields } from "./src/reducers/profile/profile";
import { interceptor } from "./src/services/api";
import * as Notifications from "expo-notifications";
import * as Updates from "expo-updates";
import injectWebCss from "./src/utils/injectWebCss";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { SafeAreaProvider } from "react-native-safe-area-context";
import MobileAppPromptModal from "./src/components/web/MobileAppPromptModal";

export const buildVersion = "451";

// Native-only notification response handler - only used on native platforms
const NotificationHandler = ({ navigationRef }) => {
  const lastNotificationResponse = Notifications.useLastNotificationResponse();

  React.useEffect(() => {
    if (lastNotificationResponse?.notification?.request) {
      if (navigationRef.isReady()) {
        navigationRef.navigate("Inbox");
      }
    }
  }, [lastNotificationResponse, navigationRef]);

  return null;
};

// Keeps user.isDoctor in sync with the role the server reports for the current
// session. Needed because the flag is persisted, so a value written by an older
// build (or by a pre-login role picker) would otherwise survive forever and put
// the user in the wrong app. Shares the ['overview'] query cache, so this adds no
// extra request when a screen has already fetched it.
const RoleSync = () => {
  const dispatch = useDispatch();
  const { loggedIn, isDoctor } = useSelector((state) => state.user);

  const { data } = useQuery({
    queryKey: ["overview"],
    queryFn: getOverview,
    enabled: !!loggedIn,
  });

  const role = data?.data?.data?.role;

  React.useEffect(() => {
    if (!role) return;
    const serverIsDoctor = role === "doctor";
    if (serverIsDoctor !== isDoctor) {
      store.dispatch(setIsDoctor(serverIsDoctor));
    }
  }, [role, isDoctor, dispatch]);

  return null;
};

const AuthInterceptorManager = ({ navigationRef }) => {
  const dispatch = useDispatch();
  const { token, loggedIn } = useSelector((state) => state.user);
  const isHandlingUnauthorized = React.useRef(false);

  React.useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }

    const handleUnauthorized = () => {
      if (isHandlingUnauthorized.current) {
        return;
      }

      isHandlingUnauthorized.current = true;

      delete api.defaults.headers.common.Authorization;

      dispatch(resetFields());
      dispatch(setLoggedOut());
      queryClient.clear();

      if (navigationRef.isReady()) {
        navigationRef.navigate("AuthScreen");
      }

      setTimeout(() => {
        isHandlingUnauthorized.current = false;
      }, 1000);
    };

    interceptor(handleUnauthorized, loggedIn);
  }, [dispatch, loggedIn, navigationRef, token]);

  return null;
};

export default function App() {
  global.XMLHttpRequest = global.originalXMLHttpRequest
    ? global.originalXMLHttpRequest
    : global.XMLHttpRequest;
  global.FormData = global.originalFormData
    ? global.originalFormData
    : global.FormData;

  fetch; // Ensure to get the lazy property
  const navigationRef = createNavigationContainerRef();
  LogBox.ignoreAllLogs(); // TODO: remove this line when you want to see the logs again
  // Text.defaultProps = Text.defaultProps || {};
  // Text.defaultProps.allowFontScaling = false;
  const appUpdate = async () => {
    try {
      const update = await Updates.checkForUpdateAsync();
      if (update.isAvailable) {
        await Updates.fetchUpdateAsync();
        // ... notify user of update ...
        Updates.reloadAsync();
      }
    } catch (err) {
      // handle or log error
      console.log(err);
    }
  };

  if (!__DEV__ && Platform.OS !== "web") appUpdate();

  if (__DEV__) {
    connectToDevTools({
      host: "localhost",
      port: 8097,
    });
  }

  // Notification setup effect - only run on native platforms
  React.useEffect(() => {
    if (Platform.OS === 'web') return;
    Notifications.setNotificationHandler({
      handleNotification: async () => ({
        shouldShowAlert: true,
        shouldPlaySound: true,
        shouldSetBadge: true,
      }),
    });
  }, []);

  React.useEffect(() => {
    store.dispatch(setIsDev(false));
    // Do NOT reset isDoctor here. It is set from the authenticated session in
    // setLoggedIn and restored by redux-persist; hardcoding it on mount clobbers
    // the real role on every page load (web) and flips the whole UI to the wrong app.
    // navigationRef.navigate("RatingScreen");

    let sub = AppState.addEventListener("change", _handleAppStateChange);
    return () => {
      sub.remove();
    };
  }, []);

  const _handleAppStateChange = (nextAppState) => {
    if (nextAppState === "active") {
      // checkUpdate();
    }
  };

  onlineManager.setEventListener((setOnline) => {
    return NetInfo.addEventListener((state) => {
      setOnline(state.isConnected);
    });
  });

  const [loaded] = useFonts({
    NotoSerif: require("./src/assets/fonts/NotoSerif-Bold.ttf"),
    "NotoSans-Bold": require("./src/assets/fonts/NotoSans-Bold.ttf"),
    "NotoSans-Regular": require("./src/assets/fonts/NotoSans-Regular.ttf"),
  });

  if (!loaded) {
    // TODO: add splashscreen
    return null;
  }
  injectWebCss();
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <SafeAreaProvider>
            <AuthInterceptorManager navigationRef={navigationRef} />
            <RoleSync />
            {Platform.OS !== 'web' && <NotificationHandler navigationRef={navigationRef} />}
            {Platform.OS === 'web' && <MobileAppPromptModal />}
            <NavigationContainer
              ref={navigationRef}
              linking={{
                config: {
                  screens: {
                    // Public pages (web) - Jobs is default for pre-login
                    PublicJobs: "",
                    PublicJobDetail: "job/:jobId",
                    LandingScreen: "home",
                    ReachUs: "contact",
                    GetApp: "getapp",
                    // Authenticated pages
                    ProfileScreen: "me",
                    PostJobScreen: "post",
                    DoctorProfileScreen: "profile",
                    JobDescriptionScreen: "job",
                    HomeScreen: {
                      screens: {
                        Home: "dashboard",
                        Inbox: "inbox",
                        Account: "account",
                      },
                    },
                    AuthScreen: {
                      screens: {
                        LoginScreen: "login",
                        RegisterScreen: "register",
                        ForgotPasswordScreen: "forgot",
                      },
                    },
                  },
                },
              }}
              documentTitle={{
                formatter: () => {
                  const isDoctor = store.getState().user.isDoctor;
                  return isDoctor ? "Supermedic Pro" : "Supermedic Facilities";
                },
              }}
              theme={{
                ...DefaultTheme,
                colors: {
                  ...DefaultTheme.colors,
                  background: "white",
                },
              }}
            >
              <StatusBar style="dark" />
              <ActionSheetProvider>
                <RootNavigation />
              </ActionSheetProvider>
            </NavigationContainer>
          </SafeAreaProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}
